import { getGroupMember } from "@api/groups";
import Button from "@components/Button";
import ConfirmDelete from "@components/ConfirmDelete";
import DropDown from "@components/Dropdown";
import ErrorFallback from "@components/ErrorFallback";
import FadedText from "@components/FadedText";
import { GalleryContext } from "@components/Gallery/contexts/GalleryContext";
import { Card, Column, Row } from "@components/Layout";
import PlayProgress from "@components/PlayProgress";
import ActionButtons from "@components/Programme/components/ActionButtons";
import { getTracklistProgress } from "@components/Programme/utils";
import ShareDialog from "@components/ShareDialog";
import { Template } from "@components/Template";
import UserIcon from "@components/UserIcon";
import useI18n from "@contexts/I18nProvider/hooks/useI18n";
import { preferencesContext } from "@contexts/Preferences";
import useProgrammeActions from "@hooks/useProgrammeActions";
import { Image } from "@kobalte/core";
import { activeFeedContext } from "@layouts/App/context/ActiveFeed";
import useFallbackUser from "@layouts/App/context/GroupRoles/hooks/useFallbackUser";
import { playerContext } from "@layouts/App/context/Player";
import PlayButton from "@layouts/App/context/Player/components/PlayButton";
import usePlay from "@layouts/App/context/Player/hooks/usePlay";
import { userContext } from "@layouts/App/context/User";
import { useNavigate } from "@solidjs/router";
import { formatDate } from "@src/app/utils";
import { FaSolidEllipsis } from "solid-icons/fa";
import { TbSortDescending } from "solid-icons/tb";
import {
	type ComponentProps,
	ErrorBoundary,
	Index,
	Show,
	type VoidComponent,
	createEffect,
	createMemo,
	createResource,
	createSignal,
	on,
	onCleanup,
	onMount,
	useContext,
} from "solid-js";
import MobileHeader from "./components/MobileHeader";
import {
	AuthorImageColumn,
	AuthorRow,
	CategoriesRow,
	CategoryPill,
	DesktopHeaderCard,
	ProgrammeName,
	TracklistHeaderColumn,
	TracklistImage,
	UserLink,
} from "./styled";
import type { HeaderProps } from "./types";

const Inner: VoidComponent<HeaderProps> = (props) => {
	const { t } = useI18n();
	const { actions, confirmDelete, handleDelete, setConfirmDelete } = useProgrammeActions();
	const { playTracklist, mixTracklist } = usePlay();
	const [share, setShare] = createSignal(false);
	const navigate = useNavigate();
	const [mixed, setMixed] = createSignal(false);
	const { getCategories } = useContext(preferencesContext);
	const { player } = useContext(playerContext);
	const { setIndex, setPhotos } = useContext(GalleryContext);
	const [categoriesWidth, setCategoriesWidth] = createSignal(0);
	const [render, setRender] = createSignal(false);
	let wrapper: undefined | HTMLDivElement;
	const { user } = useContext(userContext);
	const fallbackUser = useFallbackUser();
	const [groupUser] = createResource(
		() => props.tracklist.group,
		(g) => getGroupMember(g.uuid, user.uid, fallbackUser(user)),
	);
	const { setTracklists } = useContext(activeFeedContext);

	const handleConfirmDelete = () =>
		handleDelete(props.tracklist.uuid).then(() => {
			setTracklists((s) => s.filter((tr) => tr?.uuid !== props.tracklist.uuid));
			navigate(props.tracklist.group ? `/group/${props.tracklist.group.uuid}` : "/home");
		});

	const categories = createMemo(() => getCategories(props.tracklist.categories));

	createEffect(
		on(render, () => {
			if (render() || !wrapper) return;
			const rect = wrapper.getBoundingClientRect();
			setCategoriesWidth(rect.width);
			setRender(true);
		}),
	);

	const onWidth = () => {
		setRender(false);
	};

	onMount(() => {
		window.onresize = onWidth;
		onWidth();
	});
	onCleanup(() => window.removeEventListener("resize", onWidth));

	const onImageClick = () => {
		setPhotos([props.tracklist.photo, ...props.tracklist.tracks.map((tr) => tr.photo)]);
		setIndex(0);
	};

	return (
		<>
			<DesktopHeaderCard maxWidth="756px" padding="15px" margin={props.public ? "10px 0 0" : ""} gap="10px">
				<Row>
					<Column gap="10px" w="unset" maxWidth="100%" flexGrow={1}>
						<Row gap="20px" alignItems="stretch">
							<TracklistImage>
								<Image.Img src={props.tracklist.photo} onclick={onImageClick} />
								<Image.Fallback>
									<Template w="100%" h="100%" />
								</Image.Fallback>
							</TracklistImage>
							<TracklistHeaderColumn justifyContent="space-between" ref={wrapper} id="wrapper">
								<ProgrammeName>{props.tracklist.name}</ProgrammeName>
								<AuthorRow justifyContent="space-between" alignItems="center">
									<Row gap="25px">
										<UserLink href={`/profile/${props.tracklist.author.uid}`}>
											<Row alignItems="center" gap="15px" w="max-content">
												<AuthorImageColumn>
													<UserIcon diameter="100%" src={props.tracklist.author.photo} />
												</AuthorImageColumn>
												<Column>
													<FadedText fontWeight="bold" fontSize="15px">
														{props.tracklist.author.nickname}
													</FadedText>
													<FadedText fontSize="13px">{formatDate(props.tracklist.created_at)}</FadedText>
												</Column>
											</Row>
										</UserLink>
									</Row>
								</AuthorRow>
								<Show when={render()} fallback={<Row h="22px" />}>
									<Show when={!props.public}>
										<Row w={`${categoriesWidth()}px`} alignItems="center" justifyContent="space-between" gap="10px">
											<CategoriesRow gap="8px" h="22px">
												<Index each={categories()}>
													{(c) => (
														<CategoryPill w="max-content" h="22px" padding="0 10px" alignItems="center">
															<FadedText fontSize="13px" noWrap whiteSpace="nowrap">
																{c()}
															</FadedText>
														</CategoryPill>
													)}
												</Index>
											</CategoriesRow>
											<Show
												when={
													player.queue[player.track]?.tracklistUuid !== props.tracklist.uuid &&
													player.playing &&
													!mixed()
												}
											>
												<Button
													h="30px"
													padding="0 10px"
													onclick={() => {
														setMixed(true);
														mixTracklist(props.tracklist);
													}}
													fontSize="15px"
												>
													mix
												</Button>
											</Show>
											<button onclick={props.onReverse} type="button">
												<Row h="100%" padding="0 5px" alignItems="center">
													<FadedText>
														<TbSortDescending font-size="20px" />
													</FadedText>
												</Row>
											</button>
										</Row>
									</Show>
								</Show>
							</TracklistHeaderColumn>
						</Row>
					</Column>
					<Show when={!props.public}>
						<Column w="max-content">
							<DropDown items={actions(props.tracklist, groupUser.latest?.role_id)}>
								<FaSolidEllipsis style={{ "pointer-events": "none", "user-select": "none" }} font-size="25px" />
							</DropDown>
						</Column>
					</Show>
				</Row>
				<Row alignItems="center" gap="10px">
					<PlayButton
						diameter="40px"
						onClick={() => playTracklist(props.tracklist, 0)}
						tracklistUuid={props.tracklist.uuid}
					/>

					<PlayProgress progress={getTracklistProgress(props.tracklist)} />
				</Row>
				<Show when={!props.public}>
					<ActionButtons
						tracklist={props.tracklist}
						onShare={() => setShare(true)}
						onBookmark={props.onBookmark}
						onLike={props.onLike}
					/>
				</Show>
			</DesktopHeaderCard>
			<MobileHeader
				tracklist={props.tracklist}
				public={props.public}
				onShare={() => setShare(true)}
				onImageClick={onImageClick}
			/>
			<ConfirmDelete
				onConfirm={handleConfirmDelete}
				open={confirmDelete()}
				onOpenChange={setConfirmDelete}
				confirmText={t("app.programme.delete")}
			/>
			<ShareDialog
				tracklist={share() ? props.tracklist : undefined}
				shareOnVoxtox={!props.tracklist.group?.private}
				onOpenChange={setShare}
			/>
		</>
	);
};

const Header: VoidComponent<ComponentProps<typeof Inner>> = (props) => {
	return (
		<ErrorBoundary
			fallback={
				<Card maxWidth="756px" margin={props.public ? "10px 0 0" : ""}>
					<ErrorFallback borderRadius="16px" />
				</Card>
			}
		>
			<Inner {...props} />
		</ErrorBoundary>
	);
};
export default Header;
